import type { ComponentType, ReactNode } from 'react';
import {
  AtSign,
  BookMarked,
  Bug,
  CheckSquare,
  CircleDot,
  GitBranch,
  GitPullRequest,
  ListTodo,
  Megaphone,
  X,
} from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { MentionDetail, type MentionDetailFact } from './MentionRow';
import { cn } from '../../lib/utils';

interface ContextRefKindMeta {
  Icon: ComponentType<{ className?: string }>;
  /** Short kind noun for the hover card (e.g. 'Pull request'). */
  label: string;
}

/**
 * Display metadata for the first-party context reference kinds (see
 * `context-reference.ts` in `@runloop/reflex-contract`). The badge renders
 * inside the composer and the chat transcript — below the plugin registry —
 * so the kinds it can dress up are enumerated here; an unknown kind falls
 * back to a neutral glyph rather than failing to render.
 */
const CONTEXT_REF_KIND_META: Record<string, ContextRefKindMeta> = {
  'github-pr': { Icon: GitPullRequest, label: 'Pull request' },
  'github-issue': { Icon: CircleDot, label: 'GitHub issue' },
  'github-repo': { Icon: BookMarked, label: 'Repository' },
  'github-branch': { Icon: GitBranch, label: 'Branch' },
  'linear-issue': { Icon: ListTodo, label: 'Linear ticket' },
  'jira-issue': { Icon: CheckSquare, label: 'Jira issue' },
  'canny-post': { Icon: Megaphone, label: 'Canny idea' },
  'sentry-issue': { Icon: Bug, label: 'Sentry issue' },
};

const FALLBACK_META: ContextRefKindMeta = { Icon: AtSign, label: 'Reference' };

/** Icon + kind noun for a context reference kind, with a neutral fallback. */
export function contextRefKindMeta(kind: string): ContextRefKindMeta {
  return CONTEXT_REF_KIND_META[kind] ?? FALLBACK_META;
}

export interface ContextRefBadgeProps {
  /** The context reference kind (e.g. `github-pr`), picks the icon + noun. */
  contextKind: string;
  /** The referenced item's display title. */
  title: string;
  /** The referenced item's id, shown in the hover card and used for a stable test id. */
  refId?: string;
  /**
   * When set, the badge is a link to the referenced item, opened in a new tab.
   * Callers derive it with {@link import('./context-ref-links').contextRefTarget}.
   * Read-only surfaces (the chat transcript) pass it; the composer chip does
   * not — there a click belongs to the editor's selection, not to navigation —
   * which is also why `href` and `onRemove` never arrive together.
   */
  href?: string;
  /** Optional item details shown in the hover popover. */
  facts?: MentionDetailFact[];
  /** Fallback popover body when no `facts` are supplied. */
  description?: ReactNode;
  /** When provided, renders a remove affordance (composer chips). */
  onRemove?: () => void;
  /** Override the icon component resolved from `contextKind`. */
  IconOverride?: ComponentType<{ className?: string }>;
  /** Extra classes applied to the icon element (e.g. status colors). */
  iconClassName?: string;
  /** Extra classes applied to the badge container (e.g. status border/bg). */
  badgeClassName?: string;
  className?: string;
}

/**
 * Compact, presentational badge for a referenced context item (a pull
 * request, a Linear ticket, a Sentry issue, …). Used as a removable chip in
 * the composer and as a read-only inline badge in the chat, replacing the raw
 * `@[...](<kind>:<id>)` marker and its expanded `<referenced-context>` block.
 * Hovering opens a popover with the item's details. Pure — callers pass any
 * live details as `facts` so this stays usable across the plugin boundary.
 * The agent-run twin is {@link import('./AgentRunBadge').AgentRunBadge}.
 */
export function ContextRefBadge({
  contextKind,
  title,
  refId,
  href,
  facts,
  description,
  onRemove,
  IconOverride,
  iconClassName,
  badgeClassName,
  className,
}: ContextRefBadgeProps) {
  const { Icon: DefaultIcon, label } = contextRefKindMeta(contextKind);
  const Icon = IconOverride ?? DefaultIcon;
  // A link when the caller has a URL, a plain span otherwise. Same chrome
  // either way, so a linkless kind (an opaque provider id) doesn't read as a
  // different kind of chip.
  const Chip = href ? 'a' : 'span';
  const linkProps = href
    ? { href, target: '_blank' as const, rel: 'noopener noreferrer', 'aria-label': title }
    : {};
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <Chip
          {...linkProps}
          data-testid={`context-ref-badge-${contextKind}`}
          className={cn(
            'inline-flex max-w-full items-center gap-1 rounded-md border border-border/60 bg-muted/50 py-0.5 pr-1 pl-1.5 align-baseline text-xs text-foreground',
            href && 'hover:border-border hover:bg-muted',
            badgeClassName,
            className,
          )}
        >
          <Icon className={cn('size-3 shrink-0 text-muted-foreground', iconClassName)} />
          <span className="truncate">{title}</span>
          {onRemove ? (
            <button
              type="button"
              aria-label={`Remove ${title}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="ml-0.5 inline-flex size-3.5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="size-3" />
            </button>
          ) : null}
        </Chip>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="start" sideOffset={6} className="w-64 p-3">
        <MentionDetail
          // The card sits over the chip, so it carries the link too — pointing
          // at the reference to read its details shouldn't take the click
          // target away.
          name={
            href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                data-testid={`context-ref-badge-${contextKind}-popover-link`}
              >
                {title}
              </a>
            ) : (
              title
            )
          }
          kind={label}
          facts={facts && facts.length > 0 ? facts : undefined}
          description={!facts?.length ? (description ?? refId ?? `Referenced ${label}`) : undefined}
        />
      </HoverCardContent>
    </HoverCard>
  );
}
